import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import { Amplify } from 'aws-amplify';
import { DataStore } from '@aws-amplify/datastore';
import awsconfig from './aws-exports';

// IMPORTANT: Import models BEFORE starting DataStore so schema is registered
// This ensures IndexedDB object stores are created for all models
import './models';

// Override default auth type to use Cognito User Pools for authenticated queries
// This is required because most models use @auth(rules: [{ allow: owner }])
const amplifyConfig = {
  ...awsconfig,
  aws_appsync_authenticationType: 'AMAZON_COGNITO_USER_POOLS',
};

Amplify.configure(amplifyConfig);

// Configure DataStore to use user authentication instead of API key
// This fixes the "unauthorized" errors for owner-based models
DataStore.configure({
  authModeStrategyType: 'MULTI_AUTH',
});

// Track DataStore initialization state
let dataStoreReady = false;
let dataStoreInitializing = false;

// Initialize DataStore - this creates the IndexedDB schema
// Only clear if there's an actual error, not on every load
// Made non-blocking so app can load even if DataStore is slow
async function initializeDataStore() {
  if (dataStoreInitializing) {
    return;
  }
  
  dataStoreInitializing = true;
  
  // Run in background - don't block app loading
  (async () => {
    try {
      console.log('Initializing DataStore (non-blocking)...');
      
      // Try to start DataStore first without clearing
      // This preserves user data
      try {
        await Promise.race([
          DataStore.start(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('DataStore start timeout')), 5000)
          )
        ]);
        console.log('DataStore initialized successfully');
        dataStoreReady = true;
      } catch (startError) {
        // If start fails, check if it's a schema issue
        if (startError.message && (startError.message.includes('IDBDatabase') || startError.message.includes('object stores'))) {
          console.warn('Schema mismatch detected, clearing and recreating...');
          try {
            await DataStore.stop();
          } catch (stopError) {
            // Ignore
          }
          await DataStore.clear();
          await Promise.race([
            DataStore.start(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('DataStore start timeout')), 5000)
            )
          ]);
          console.log('DataStore recovered successfully');
          dataStoreReady = true;
        } else {
          console.warn('DataStore initialization failed (non-critical):', startError.message);
          dataStoreReady = false;
        }
      }
    } catch (error) {
      console.warn('DataStore initialization error (non-critical):', error);
      dataStoreReady = false;
    } finally {
      dataStoreInitializing = false;
    }
  })();
}

// Helper to wait for DataStore to be ready (exported for use in other files if needed)
// Note: This is a simple implementation - in practice, components should wait before querying

// Start DataStore initialization (don't block app rendering)
initializeDataStore();

// Lock theme to Cyber for production builds
document.documentElement.setAttribute('data-theme', 'cyber');
try {
  localStorage.removeItem('tekyu_theme');
} catch {
  // ignore
}

const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);